/* import __COLOCATED_TEMPLATE__ from './lead-details-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action, get as emberGet } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type { MemoryFieldColumn } from 'embercom/lib/ai-automation/sdr-memory-fields';
import { toDisplayLabel, toUserAttributeKey } from 'embercom/lib/ai-automation/sdr-memory-fields';
import type User from 'embercom/models/user';
import type IntlService from 'embercom/services/intl';
import type { MeetingBooking } from 'embercom/lib/ai-automation/sdr-meeting-booking';

type UserInstance = InstanceType<typeof User>;

const CORE_COMPANY_IDENTIFIERS = [
  'company.name',
  'company.industry',
  'company.size',
  'company.website',
  'company.monthly_spend',
] as const;

const CORE_COMPANY_FIELDS: MemoryFieldColumn[] = CORE_COMPANY_IDENTIFIERS.map((identifier) => ({
  identifier,
  key: toUserAttributeKey(identifier),
  label: toDisplayLabel(identifier),
  isCompany: true,
}));

const PROVIDER_CALENDLY = 'calendly';
const PROVIDER_CHILI_PIPER_CONCIERGE = 'chilli_piper_concierge';

const ENTRY_SOURCE_QUALIFICATION = 'qualification';
const ENTRY_SOURCE_MESSENGER_BUTTON = 'messenger_button';

type ResolvedField = {
  label: string;
  value: string;
};

interface Signature {
  Args: {
    user: UserInstance;
    dynamicColumns: MemoryFieldColumn[];
    booking: MeetingBooking | null;
    isLoadingBooking: boolean;
    bookingFetchFailed: boolean;
    showMeetingSection: boolean;
    onClose: () => void;
  };
  Blocks: {};
  Element: null;
}

export default class LeadDetailsDrawer extends Component<Signature> {
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked leadSectionOpen = true;
  @tracked companySectionOpen = true;
  @tracked meetingSectionOpen = true;

  private resolveFields(columns: MemoryFieldColumn[]): ResolvedField[] {
    let user = this.args.user;
    return columns.map((col) => {
      let raw = emberGet(user, col.key as keyof UserInstance);
      let value = raw != null && raw !== '' ? String(raw) : '-';
      return { label: col.label, value };
    });
  }

  get leadFields(): ResolvedField[] {
    return this.resolveFields(this.args.dynamicColumns.filter((c) => !c.isCompany));
  }

  get companyFields(): ResolvedField[] {
    let memoryFields = this.args.dynamicColumns.filter((c) => c.isCompany);
    let memoryIdentifiers = new Set(memoryFields.map((f) => f.identifier));
    let extraFields = CORE_COMPANY_FIELDS.filter((f) => !memoryIdentifiers.has(f.identifier));
    return this.resolveFields([...memoryFields, ...extraFields]).map((field) => ({
      ...field,
      label: field.label.replace(/^Company\s+/, '').replace(/^\w/, (c) => c.toUpperCase()),
    }));
  }

  private get userBookedAt(): string | null {
    let bookedAt = (this.args.user as $TSFixMe)?.custom_data?.fin_meeting_booked_at;
    return typeof bookedAt === 'string' && bookedAt.length > 0 ? bookedAt : null;
  }

  get hasBooking(): boolean {
    return this.userBookedAt !== null;
  }

  get bookingDetailsAvailable(): boolean {
    return (
      this.args.booking != null && !this.args.isLoadingBooking && !this.args.bookingFetchFailed
    );
  }

  get bookingStatusLabel(): string {
    return this.hasBooking
      ? this.intl.t('ai-agent.analyze.leads.drawer.meeting.booked')
      : this.intl.t('ai-agent.analyze.leads.drawer.meeting.no-booking');
  }

  get providerLabel(): string {
    let booking = this.args.booking;
    if (!booking) {
      return '';
    }
    if (booking.provider === PROVIDER_CALENDLY) {
      return this.intl.t('ai-agent.analyze.leads.drawer.meeting.provider.calendly');
    }
    if (booking.provider === PROVIDER_CHILI_PIPER_CONCIERGE) {
      return this.intl.t('ai-agent.analyze.leads.drawer.meeting.provider.chili-piper');
    }
    return booking.provider;
  }

  get sourceLabel(): string {
    let booking = this.args.booking;
    if (!booking) {
      return '';
    }
    if (booking.entry_source === ENTRY_SOURCE_QUALIFICATION) {
      return this.intl.t('ai-agent.analyze.leads.drawer.meeting.source.qualification');
    }
    if (booking.entry_source === ENTRY_SOURCE_MESSENGER_BUTTON) {
      return this.intl.t('ai-agent.analyze.leads.drawer.meeting.source.messenger-button');
    }
    return booking.entry_source ?? '';
  }

  get sourceIsQualification(): boolean {
    return this.args.booking?.entry_source === ENTRY_SOURCE_QUALIFICATION;
  }

  get sourceIsMessengerButton(): boolean {
    return this.args.booking?.entry_source === ENTRY_SOURCE_MESSENGER_BUTTON;
  }

  get bookedAtDate(): string {
    let bookedAt = this.userBookedAt;
    if (!bookedAt) {
      return '';
    }
    return this.intl.formatDate(bookedAt, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  get bookedAtFullTimestamp(): string {
    let bookedAt = this.userBookedAt;
    if (!bookedAt) {
      return '';
    }
    return this.intl.formatDate(bookedAt, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    });
  }

  @action
  toggleLeadSection() {
    this.leadSectionOpen = !this.leadSectionOpen;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'toggled',
      object: 'lead_section',
      place: 'fin_sdr_qualification_analyze',
      section: 'leads_drawer',
      expanded: this.leadSectionOpen,
    });
  }

  @action
  toggleCompanySection() {
    this.companySectionOpen = !this.companySectionOpen;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'toggled',
      object: 'company_section',
      place: 'fin_sdr_qualification_analyze',
      section: 'leads_drawer',
      expanded: this.companySectionOpen,
    });
  }

  @action
  toggleMeetingSection() {
    this.meetingSectionOpen = !this.meetingSectionOpen;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'toggled',
      object: 'meeting_section',
      place: 'fin_sdr_qualification_analyze',
      section: 'leads_drawer',
      expanded: this.meetingSectionOpen,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAutomation::Analyze::Leads::LeadDetailsDrawer': typeof LeadDetailsDrawer;
    'ai-automation/analyze/leads/lead-details-drawer': typeof LeadDetailsDrawer;
  }
}
